import { UserProfile, useClerk, useUser } from '@clerk/clerk-react';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Required-MFA gate for school-staff accounts. Rendered by ProtectedRoute
 * (after the wrong-portal check passes) when VITE_REQUIRE_STAFF_MFA=1 and
 * the signed-in user has no second factor enrolled.
 *
 * Staff accounts hold student PII, so they warrant a second factor even
 * though student accounts don't. Enforcement is instance-wide opt-in:
 * the Clerk instance must have TOTP/backup codes enabled (Dashboard →
 * Configure → Multi-factor) BEFORE the env flag is set, or the Security
 * section below won't offer an "Add two-step verification" action.
 *
 * Enrollment UI is Clerk's own <UserProfile> (Security tab) — it handles
 * the QR code, verification, and backup codes. Clerk's useUser() is
 * reactive, so the moment enrollment completes, `user.twoFactorEnabled`
 * flips and ProtectedRoute re-renders the app; no manual "re-check" step.
 *
 * This is a UX-layer gate (like ProtectedRoute itself). Server-side
 * enforcement (rejecting tokens without a second-factor claim) is a
 * follow-up that needs the session-token template extended first.
 */
export default function MfaEnrollmentGate({ onDefer }: { onDefer?: () => void }) {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  const { user } = useUser();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-10 gap-6">
      <div className="max-w-xl text-center flex flex-col items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-primary">
          <ShieldCheck size={24} />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('mfa.title', { defaultValue: 'Set up two-step verification' })}
        </h1>
        <p className="text-sm text-gray-600">
          {t('mfa.body', {
            defaultValue:
              'School staff accounts can access student records, so your school requires a second sign-in step. Open the Security section below and add an authenticator app — it takes about a minute, and you only do this once. You can also do it later.',
          })}
        </p>
      </div>

      {/* Clerk's own profile component hosts the enrollment flow. The
          Security tab is where "Add two-step verification" lives. */}
      <UserProfile routing="hash" />

      <div className="flex flex-col items-center gap-3">
        {onDefer ? (
          <button
            type="button"
            onClick={onDefer}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {t('mfa.remindLater', { defaultValue: "I'll do this later" })}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void signOut({ redirectUrl: '/signin' })}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 underline"
        >
          {t('mfa.signOut', {
            defaultValue: 'Sign out',
          })}
          {user?.primaryEmailAddress ? ` (${user.primaryEmailAddress.emailAddress})` : ''}
        </button>
      </div>
    </div>
  );
}
